import { FC, useContext, useEffect, useCallback, useState, MouseEvent } from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'

import { ModuleContext } from '../../contexts/ModuleContext'
import { useLMSContext } from '../../contexts/LMSContext'
import { useTroubleshootContext, TroubleshootActionType } from '../../contexts/TroubleshootContext'

import './WorkOrder.scss'
import WorkOrderSheet from './WorkOrderSheet'
import NavigationBar from '../NavigationBar'
import * as Solver from '../../Solver'
import * as LMS from '../../LMS'

import { isDebug } from '../../App.config'
import { moduleContent } from '../../Module.config'
import { ButtonGroup, PrimaryButton, SecondaryButton } from '../../styles/Buttons'
import { WorkOrder } from '../../Types'
import { preventContextMenu } from '../../Utils'


const WorkOrderPage: FC = () => {
    const context = useContext(ModuleContext)
    const { user, connection, groupName, solverAvailable } = context
    const { lmsState: { isLMSAvailable } } = useLMSContext()
    const history = useHistory()
    const [faultIsLoaded, setFaultIsLoaded] = useState(false)
    const [workOrder, setWorkOrder] = useState<WorkOrder>({} as WorkOrder)
    const [solverCallBackLoaded, setSolverCallBackLoaded] = useState(false)
    const { tsState, tsDispatch } = useTroubleshootContext()

    const noWorkOrder = !workOrder.Name

    const updateWorkOrder = useCallback((data: string) => {
        try {
            const newWorkOrder = JSON.parse(data) as WorkOrder
            isDebug && console.log('%cWork Order update:', Solver.solverCSS, newWorkOrder)
            setWorkOrder(newWorkOrder)
        }
        catch (error) {
            console.error('Error parsing Work Order from Solver.')
        }
    }, [])

    const registerSolverCallback = useCallback(() => {
        if (solverCallBackLoaded) return
        isDebug && console.log('Registering Work Order solver callback...')
        connection.on('clientWorkOrderUpdate', updateWorkOrder)
        setSolverCallBackLoaded(true)
    }, [connection, solverCallBackLoaded, updateWorkOrder])

    const unregisterSolverCallback = useCallback(() => {
        isDebug && console.log('UNREGISTER Work Order solver callback...')
        connection.off('clientWorkOrderUpdate', updateWorkOrder)
    }, [connection, updateWorkOrder])

    const handleCancel = (event: any) => {
        event.preventDefault()
        if (noWorkOrder) return
        handleUnload()
        history.goBack()
    }

    const handleBegin = (event: any) => {
        event.preventDefault()
        if (noWorkOrder) return
        event.target.disabled = true
        Solver.beginFault(connection, groupName)
            .then(() => {
                isDebug && console.log(`Fault began while Fault ID in Context is ${tsState.faultId}.`)
                isLMSAvailable && LMS.incrementScore(1)
                unregisterSolverCallback()
                tsDispatch({ type: TroubleshootActionType.SAVE_FAULT_HISTORY, payload: undefined })
                tsDispatch({ type: TroubleshootActionType.SAVE_SCOREBOARD, payload: undefined })
                history.push('/simulation')
            })
    }

    const handleUnload = useCallback(() => {
        isDebug && console.log('Work order unload triggered.')
        isDebug && console.log(`Telling solver to Exit Fault while Fault ID in context is ${tsState.faultId}`)
        unregisterSolverCallback()
        Solver.exitFault(connection, groupName).then(() => {
            isDebug && console.log(`Exited fault with Fault ID ${tsState.faultId} in TSContext.`)
            context.faultID = 'F000' //FIXME: Don't set context directly
        })
    }, [connection, context, groupName, tsState.faultId, unregisterSolverCallback])

    useEffect(() => {
        if (faultIsLoaded || !solverAvailable) return
        isDebug && !tsState.faultId && console.warn('No fault ID supplied. Using random fault.')
        const newFaultId = !tsState.faultId ? 'FRND' : tsState.faultId
        context.faultID = newFaultId //TODO: This is bad, but the faultID in ModuleContext is still needed in Simulation and UnityComponent until they are converted to React.FC with hooks.        
        const loadFaultID = async (fid: any) => await Solver.loadFault(connection, groupName, user.moduleId, fid, user.electricalCode)

        loadFaultID(newFaultId)
            .then(() => setFaultIsLoaded(true))

    }, [connection, context, faultIsLoaded, groupName, solverAvailable, tsState.faultId, user.electricalCode, user.moduleId])

    useEffect(() => {
        window.addEventListener('unload', handleUnload)
        if (!solverCallBackLoaded) registerSolverCallback()
        return () => window.removeEventListener('unload', handleUnload)
    }, [handleUnload, registerSolverCallback, solverCallBackLoaded])

    return (
        <div className="work-order-page one-hundred body-override" onContextMenu={(e: MouseEvent) => preventContextMenu(e)}>
            <NavigationBar pageTitle={moduleContent.title} onHomeClick={handleUnload} />
            <Container className={'one-hundred'}>
                <Row>
                    <Col>
                        <div className={'center-all column'}>
                            <WorkOrderSheet workOrder={workOrder} />

                            <ButtonGroup>
                                <PrimaryButton disabled={noWorkOrder} onClick={handleCancel}>
                                    {'CANCEL'}
                                </PrimaryButton>
                                <SecondaryButton disabled={noWorkOrder} onClick={handleBegin}>
                                    {'BEGIN'}
                                </SecondaryButton>
                            </ButtonGroup>
                        </div>
                    </Col>
                </Row>
            </Container>
        </div>
    )

}

export default WorkOrderPage